/**
 * Activity duration units — match iOS SyncMigration / activityDuration contract.
 * Wire: metadata.duration = seconds (string). UI: minutes.
 */

/** Convert UI minutes → metadata.duration seconds string (iOS Add/Edit). */
export function minutesToMetadataDurationSeconds(minutes: number): string {
  return String(Math.round(minutes * 60));
}

/**
 * Normalize raw metadata.duration numeric value to seconds.
 * Mirrors iOS SyncMigration.normalizeActivityDurationUnits:
 * - <= 0 → null (drop)
 * - > 86400 → / 3600 (compounded hours bug)
 * - <= 720 → * 60 (legacy minutes)
 * - else already seconds
 * Clamped to 1…86400.
 */
export function normalizeMetadataDurationToSeconds(raw: number): number | null {
  if (!Number.isFinite(raw) || raw <= 0) return null;

  let seconds: number;
  if (raw > 86400) {
    seconds = raw / 3600;
  } else if (raw <= 720) {
    seconds = raw * 60;
  } else {
    seconds = raw;
  }

  const clamped = Math.max(1, Math.min(seconds, 86400));
  return Math.round(clamped);
}

/** Seconds → whole minutes for UI (toward zero, like iOS ActivityDurationFormatting). */
export function metadataDurationSecondsToMinutes(seconds: number): number {
  const clamped = Math.max(0, Math.min(seconds, 86400));
  return Math.floor(clamped / 60);
}

/** Parse metadata.duration string → minutes for UI, or undefined. */
export function durationMinutesFromMetadata(
  metadata: Record<string, string> | null | undefined
): number | undefined {
  if (!metadata?.duration) return undefined;
  const raw = Number.parseFloat(metadata.duration);
  if (!Number.isFinite(raw)) return undefined;
  const seconds = normalizeMetadataDurationToSeconds(raw);
  if (seconds == null) return undefined;
  const minutes = metadataDurationSecondsToMinutes(seconds);
  return minutes > 0 ? minutes : undefined;
}

/** Format minutes for Day View / cards: "Xh Ym", "Xh", or "Ym". */
export function formatDurationMinutes(minutes: number): string {
  const total = Math.max(0, Math.floor(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}
